<template>
  <div class="border border-gray-200 rounded-lg p-4 bg-gray-50 mt-6">
    <h3 class="font-bold text-gray-700 mb-3 border-b pb-2">
      Additional Attachments
    </h3>

    <div class="space-y-4">
      <!-- Lista de archivos adjuntos -->
      <div v-if="files.length > 0" class="space-y-2">
        <div
          v-for="(file, index) in files"
          :key="index"
          class="flex items-center justify-between p-2 bg-white rounded border border-gray-200"
        >
          <div class="flex items-center space-x-2 truncate">
            <span class="text-gray-700 truncate">{{ file.name }}</span>
            <span class="text-xs text-gray-500 whitespace-nowrap"
              >({{ formatFileSize(file.size) }})</span
            >
          </div>
          <button
            @click="removeFile(index)"
            class="text-red-500 hover:text-red-600 p-1"
            title="Remove file"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- Dropzone add files -->
      <div
        @dragover.prevent="dragover = true"
        @dragleave.prevent="dragover = false"
        @drop.prevent="handleDrop"
        @click="triggerFileInput"
        :class="{ 'border-accent-500 bg-accent-50': dragover }"
        class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer transition-colors"
      >
        <input
          type="file"
          ref="fileInput"
          @change="handleFileSelect"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.gif"
          class="hidden"
        />

        <div class="flex flex-col items-center justify-center space-y-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-10 w-10 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          <p class="text-sm text-gray-600">
            <span class="font-medium text-brand-700">Click to upload</span> or
            drag and drop
          </p>
          <p class="text-xs text-gray-500">
            PDF, JPG, PNG, GIF (Max 20MB each)
          </p>
        </div>
      </div>

      <div v-if="error" class="text-red-500 text-sm mt-2">{{ error }}</div>
    </div>
  </div>
</template>
  
  <script>
import { ref, watch } from "vue";

export default {
  name: "FileAttachments",
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const files = ref([...props.modelValue]);
    const dragover = ref(false);
    const error = ref(null);
    const fileInput = ref(null);

    const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
    const ACCEPTED_TYPES = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/gif",
    ];

    const triggerFileInput = () => {
      fileInput.value.click();
    };

    const emitAttachments = () => {
      emit("update-attachments", files.value);
    };

    const handleFileSelect = (event) => {
      const selectedFiles = Array.from(event.target.files);
      processFiles(selectedFiles);
      event.target.value = "";
    };

    const handleDrop = (event) => {
      dragover.value = false;
      const droppedFiles = Array.from(event.dataTransfer.files);
      processFiles(droppedFiles);
    };

    const processFiles = (newFiles) => {
      error.value = null;

      for (const file of newFiles) {
        if (file.size > MAX_FILE_SIZE) {
          error.value = `File "${file.name}" exceeds maximum size of 10MB`;
          return;
        }

        if (!ACCEPTED_TYPES.includes(file.type)) {
          error.value = `File type not supported for "${file.name}"`;
          return;
        }
      }

      // Merge new files with existing ones
      const updatedFiles = [...files.value, ...newFiles];
      files.value = updatedFiles;
      emit("update:modelValue", updatedFiles);
    };

    const removeFile = (index) => {
      const updatedFiles = files.value.filter((_, i) => i !== index);
      files.value = updatedFiles;
      emit("update:modelValue", updatedFiles);
    };

    const formatFileSize = (bytes) => {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2) + " " + sizes[i]);
    };

    // Sync with parent when modelValue changes
    watch(
      () => props.modelValue,
      (newValue) => {
        if (JSON.stringify(newValue) !== JSON.stringify(files.value)) {
          files.value = [...newValue];
        }
      }
    );

    watch(files, emitAttachments, { deep: true });

    return {
      files,
      dragover,
      error,
      fileInput,
      triggerFileInput,
      handleFileSelect,
      handleDrop,
      removeFile,
      formatFileSize,
    };
  },
};
</script>
  
  <style scoped>
.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}
</style>